import { useState } from 'react';
import { adminAPI } from '../services/api';
import JsonViewer from '../components/JsonViewer';
import { ExclamationTriangleIcon, MagnifyingGlassIcon, TrashIcon } from '@heroicons/react/24/outline';

export default function UserDeletionPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [deleteResult, setDeleteResult] = useState(null);
  const [error, setError] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  const handleLookup = async () => {
    if (!email) {
      setError('Please enter an email address');
      return;
    }

    setLoading(true);
    setError(null);
    setLookupResult(null);
    setDeleteResult(null);

    try {
      const response = await adminAPI.lookupUser(email);
      setLookupResult(response.data);
      console.log('[User Lookup] Success:', response.data);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to lookup user';
      setError(errorMessage);
      console.error('[User Lookup] Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      setError('Please type "DELETE" to confirm');
      return;
    }

    setLoading(true);
    setError(null);
    setDeleteResult(null);

    try {
      const response = await adminAPI.deleteUserByEmail(email);
      setDeleteResult(response.data);
      setLookupResult(null);
      setConfirmText('');
      console.log('[User Deletion] Success:', response.data);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete user';
      setError(errorMessage);
      console.error('[User Deletion] Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-deep-navy mb-2">
          User Deletion Tool
        </h1>
        <p className="text-gray-600">
          Completely remove a user from the system including PostgreSQL, Firebase Auth, and Firestore.
        </p>
      </div>

      {/* Warning Banner */}
      <div className="card bg-red-50 border-2 border-red-300 mb-6">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-red-900 mb-2">DESTRUCTIVE OPERATION</h3>
            <ul className="text-sm text-red-800 space-y-1">
              <li>Deletes user from PostgreSQL (all related records)</li>
              <li>Deletes user from Firebase Authentication</li>
              <li>Deletes user's chat messages from Firestore</li>
              <li>This operation is IRREVERSIBLE</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Email Input */}
      <div className="card mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          Step 1: Enter Email
        </h2>

        <div className="flex gap-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input flex-1"
            placeholder="user@example.com"
          />
          <button
            onClick={handleLookup}
            disabled={loading || !email}
            className="btn-primary flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
          >
            <MagnifyingGlassIcon className="w-5 h-5" />
            {loading ? 'Looking up...' : 'Lookup User'}
          </button>
        </div>
      </div>

      {/* Lookup Results */}
      {lookupResult && (
        <div className="card mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Step 2: Review User Data
          </h2>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* PostgreSQL Data */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">PostgreSQL</h3>
              {lookupResult.postgres ? (
                <ul className="text-sm text-blue-800 space-y-1">
                  <li><strong>ID:</strong> {lookupResult.postgres.id}</li>
                  <li><strong>Name:</strong> {lookupResult.postgres.name}</li>
                  <li><strong>Status:</strong> {lookupResult.postgres.status}</li>
                  <li><strong>Organizations:</strong> {lookupResult.postgres.org_count}</li>
                  <li><strong>Groups:</strong> {lookupResult.postgres.group_count}</li>
                  <li><strong>Prayers:</strong> {lookupResult.postgres.prayer_count}</li>
                  <li><strong>Attendance:</strong> {lookupResult.postgres.attendance_count}</li>
                </ul>
              ) : (
                <p className="text-gray-500 italic">No PostgreSQL record found</p>
              )}
            </div>

            {/* Firebase Data */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h3 className="font-semibold text-orange-900 mb-2">Firebase Auth</h3>
              {lookupResult.firebase ? (
                <ul className="text-sm text-orange-800 space-y-1">
                  <li><strong>UID:</strong> {lookupResult.firebase.uid}</li>
                  <li><strong>Display Name:</strong> {lookupResult.firebase.displayName || 'N/A'}</li>
                  <li><strong>Email Verified:</strong> {lookupResult.firebase.emailVerified ? 'Yes' : 'No'}</li>
                  <li><strong>Created:</strong> {lookupResult.firebase.creationTime}</li>
                  <li><strong>Last Sign In:</strong> {lookupResult.firebase.lastSignInTime || 'Never'}</li>
                </ul>
              ) : (
                <p className="text-gray-500 italic">No Firebase record found</p>
              )}
            </div>
          </div>

          {/* Delete Confirmation */}
          <div className="border-t pt-4 mt-4">
            <h3 className="font-semibold text-gray-800 mb-3">Step 3: Confirm Deletion</h3>

            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label htmlFor="confirm" className="label">
                  Type "DELETE" to confirm
                </label>
                <input
                  id="confirm"
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="input"
                  placeholder="DELETE"
                />
              </div>
              <button
                onClick={handleDelete}
                disabled={loading || confirmText !== 'DELETE'}
                className={`btn-primary flex items-center gap-2 ${
                  confirmText !== 'DELETE'
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                <TrashIcon className="w-5 h-5" />
                {loading ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Messages */}
      {error && (
        <div className="mb-6 bg-danger-50 border border-danger-200 text-danger-800 px-4 py-3 rounded-lg">
          <p className="font-semibold">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Success Messages */}
      {deleteResult && (
        <div className="mb-6 bg-success-50 border border-success-200 text-success-800 px-4 py-3 rounded-lg">
          <p className="font-semibold">User Deleted Successfully</p>
          <p className="text-sm">{deleteResult.message}</p>
        </div>
      )}

      {/* Detailed Results */}
      {deleteResult && <JsonViewer data={deleteResult} title="Deletion Details" />}

      {/* API Documentation */}
      <div className="card bg-gray-50 mt-6">
        <h3 className="font-semibold text-storm-blue mb-3">API Endpoints</h3>
        <ul className="text-sm text-gray-700 space-y-2">
          <li>
            <code className="bg-blue-100 px-1 py-0.5 rounded">GET /api/admin/users/lookup?email=...</code>
            <span className="text-gray-500 ml-2">- Look up user details</span>
          </li>
          <li>
            <code className="bg-red-100 px-1 py-0.5 rounded">DELETE /api/admin/users/delete-by-email</code>
            <span className="text-gray-500 ml-2">- Delete user completely</span>
          </li>
        </ul>
      </div>

      {/* What Gets Deleted */}
      <div className="card bg-yellow-50 border border-yellow-200 mt-6">
        <h3 className="font-semibold text-yellow-900 mb-3">What Gets Deleted</h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm text-yellow-800">
          <div>
            <h4 className="font-medium mb-1">PostgreSQL</h4>
            <ul className="space-y-0.5 text-xs">
              <li>• users record</li>
              <li>• user_organizations</li>
              <li>• user_roles</li>
              <li>• group_members</li>
              <li>• prayer_history</li>
              <li>• attendance records</li>
              <li>• event_rsvps</li>
              <li>• invitations</li>
              <li>• fcm_tokens</li>
              <li>• notification_history</li>
              <li>• user_sessions</li>
              <li>• password_reset_tokens</li>
              <li>• email_verification_tokens</li>
              <li>• person_notes</li>
              <li>• user_children</li>
              <li>• family_relationships</li>
              <li>• household membership</li>
              <li className="text-yellow-600 italic">• audit_logs (nullified, not deleted)</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium mb-1">Firebase Auth</h4>
            <ul className="space-y-0.5 text-xs">
              <li>• User account</li>
              <li>• Authentication credentials</li>
              <li>• Custom claims</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium mb-1">Firestore</h4>
            <ul className="space-y-0.5 text-xs">
              <li>• Chat messages</li>
            </ul>
            <h4 className="font-medium mb-1 mt-3">Compliance</h4>
            <ul className="space-y-0.5 text-xs">
              <li>• Audit log entry created</li>
              <li>• GDPR/CCPA compliant</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
